import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetTrigger,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { NotebookPen, Loader2, Languages } from 'lucide-react';
import useTranslate from '../hooks/useTranslate';
import { usePremiumGate } from '../hooks/usePremiumGate';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Tyndale Open Study Notes trigger — modern study notes (CC BY-SA) for the
 * reader's current chapter. Sibling to the other classic-commentary buttons.
 */
const TyndaleCommentaryButton = ({ book, chapter, language = 'en' }) => {
  const [open, setOpen] = useState(false);
  const { requirePremium, dialog: premiumDialog } = usePremiumGate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !book || !chapter) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    axios.get(`${BACKEND_URL}/api/resources/tyndale/${encodeURIComponent(book)}/${chapter}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.status === 404
          ? (language === 'es' ? 'Notas no disponibles para este capítulo' : 'Notes not available for this chapter')
          : (language === 'es' ? 'Error al cargar' : 'Failed to load'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, book, chapter, language]);

  const { output: tText, loading: tLoading, showOriginal, setShowOriginal, isTranslated } =
    useTranslate([data?.text || ''], language);

  return (
    <>
    <Sheet open={open} onOpenChange={(v) => { if (v && !requirePremium(language === 'es' ? 'Notas de Estudio Tyndale' : 'Tyndale Study Notes')) return; setOpen(v); }}>
      <SheetTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2.5 text-xs gap-1.5 border border-rose-300/50 bg-rose-50/50 hover:bg-rose-100/70 text-rose-900 dark:bg-rose-950/30 dark:text-rose-200 dark:border-rose-800/50 dark:hover:bg-rose-900/50"
          data-testid="tyndale-open-btn"
          title={language === 'es' ? 'Notas de Estudio Tyndale' : 'Tyndale Study Notes'}
        >
          <NotebookPen className="w-3.5 h-3.5" />
          <span className="hidden sm:inline font-medium">Tyndale</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-hidden flex flex-col p-0" data-testid="tyndale-sheet">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-rose-200/60 dark:border-rose-800/40">
          <SheetTitle className="text-rose-900 dark:text-rose-100 flex items-center gap-2">
            <NotebookPen className="w-5 h-5" />
            {language === 'es' ? 'Tyndale · Notas de Estudio' : 'Tyndale · Study Notes'}
          </SheetTitle>
          <SheetDescription className="text-xs">
            {book} {chapter}{' · '}
            {language === 'es' ? 'Notas de estudio modernas · CC BY-SA' : 'Modern study notes · CC BY-SA'}
          </SheetDescription>
          {isTranslated && data && (
            <Button onClick={() => setShowOriginal((v) => !v)} size="sm" variant="ghost"
              className="h-7 px-2 mt-1 w-fit text-[11px] font-semibold text-rose-800 hover:bg-rose-100/60 dark:text-rose-200 dark:hover:bg-rose-900/40"
              data-testid="tyndale-translate-toggle" disabled={tLoading}>
              {tLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Languages className="w-3 h-3 mr-1" />}
              {showOriginal ? 'Ver traducción' : 'Ver original'}
            </Button>
          )}
        </SheetHeader>
        <ScrollArea className="flex-1 px-5 py-4">
          {loading && <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>}
          {error && !loading && <p className="text-sm text-slate-600 dark:text-slate-400 italic">{error}</p>}
          {data && !loading && (
            <div className="space-y-3 text-sm leading-relaxed whitespace-pre-line text-slate-800 dark:text-slate-200">{tText[0]}</div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
    {premiumDialog}
    </>
  );
};

export default TyndaleCommentaryButton;
